import React from 'react';
import PropTypes from 'prop-types';
import t from './tokens';

// Post-add sibling cross-sell (Shoob v2 Figma "Package added" confirmation): after
// adding for one student, nudge the parent to order for their other children in the
// same session (siblings linked by shared email/phone). "Shop their photos" switches
// to that student's gallery (onShop); "Continue to cart" proceeds (onSkip). Only
// shown when there are other students, so the cross-sell always has somewhere to go.
const s = {
  page: { background: t.pageBg, minHeight: '100vh', padding: '2.5rem 1.25rem' },
  inner: { maxWidth: 640, margin: '0 auto', textAlign: 'center' },
  check: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: t.success,
    color: '#fff',
    fontSize: '1.9rem',
    lineHeight: '56px',
    margin: '0 auto 1rem',
    fontWeight: 700,
  },
  h1: {
    fontSize: 'clamp(1.4rem,3vw,1.85rem)', fontWeight: 700, color: t.ink, margin: '0 0 0.4rem',
  },
  sub: { color: t.muted, margin: '0 0 2rem', fontSize: '1rem' },
  sectionLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: t.muted,
    margin: '0 0 0.75rem',
  },
  list: {
    display: 'flex', flexDirection: 'column', gap: '0.6rem', textAlign: 'left',
  },
  sibCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    background: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: t.radiusLg,
    boxShadow: t.shadowSm,
    padding: '0.95rem 1.1rem',
    cursor: 'pointer',
    font: 'inherit',
  },
  sibName: { fontWeight: 700, color: t.ink, fontSize: '1.05rem' },
  sibCta: { color: t.brand, fontWeight: 700, fontSize: '0.9rem' },
  primary: {
    marginTop: '1.75rem',
    width: '100%',
    maxWidth: 320,
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.8rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '1rem',
  },
};

const PackageAddedConfirmation = ({
  studentName, siblings, adding, onShop, onSkip,
}) => {
  const childWord = siblings.length === 1 ? 'child' : 'children';
  return (
    <div style={s.page}>
      <div style={s.inner}>
        <div style={s.check} aria-hidden="true">✓</div>
        <h1 style={s.h1}>{studentName ? `Added to ${studentName}'s order` : 'Added to your order'}</h1>
        <p style={s.sub}>{`You have photos for your other ${childWord}, too — order for everyone in one checkout.`}</p>

        <div style={s.sectionLabel}>{`Order for your other ${childWord}`}</div>
        <div style={s.list}>
          {siblings.map((sb) => (
            <button
              key={sb.student_image_id}
              type="button"
              style={s.sibCard}
              disabled={adding}
              onClick={() => onShop(sb.student_image_id)}
            >
              <span style={s.sibName}>{sb.name}</span>
              <span style={s.sibCta}>Shop their photos →</span>
            </button>
          ))}
        </div>

        <button type="button" style={s.primary} disabled={adding} onClick={onSkip}>
          Continue to cart
        </button>
      </div>
    </div>
  );
};

PackageAddedConfirmation.propTypes = {
  studentName: PropTypes.string,
  siblings: PropTypes.arrayOf(PropTypes.object).isRequired,
  adding: PropTypes.bool,
  onShop: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

PackageAddedConfirmation.defaultProps = { studentName: null, adding: false };

export default PackageAddedConfirmation;
